/**
 * Registry of admin modules that platform admins can enable / disable
 * per tenant. Each entry maps to a top-level sidebar item in nav.ts.
 *
 * Some modules are CORE — they can't be disabled (Dashboard, Settings,
 * Manage Admin, Admin Profile). Everything else is opt-in / opt-out at
 * the platform-admin level.
 */

export interface ModuleDef {
  key: string;
  label: string;
  description: string;
  category: "core" | "ops" | "finance" | "compliance" | "ai" | "trading" | "marketing";
  core?: boolean;          // can't be disabled
  defaultEnabled?: boolean;
}

export const MODULE_REGISTRY: ModuleDef[] = [
  // Core (always on)
  { key: "dashboard",        label: "Dashboard",          description: "Top-line stats on the broker landing page.", category: "core", core: true },
  { key: "admin-profile",    label: "Admin Profile",      description: "Admin's own settings, password, sessions.", category: "core", core: true },
  { key: "manage-admin",     label: "Manage Admin",       description: "Admin groups, list, rights.", category: "core", core: true },
  { key: "settings",         label: "Tenant Settings",    description: "Branding, SMTP, payment gateway, integrations.", category: "core", core: true },

  // Operations
  { key: "manage-client",    label: "Manage Trader",      description: "Trader admin, analysis, and bulk management.", category: "ops" },
  { key: "manage-request",   label: "Manage Request",     description: "Bank, deposit, withdraw, KYC, IB, transfer queues.", category: "ops" },
  { key: "kyc-management",   label: "KYC Management",     description: "KYC settings, upload, reports, review queue.", category: "ops" },
  { key: "lead-management",  label: "Lead Management",    description: "Sales lead pipeline + assignment.", category: "ops" },
  { key: "manager-settings", label: "Manager Settings",   description: "Sales / support manager teams.", category: "ops" },
  { key: "support",          label: "Support Tickets",    description: "Trader-facing ticket system.", category: "ops" },
  { key: "email",            label: "Bulk Email",         description: "Mass email campaigns to traders.", category: "ops" },
  { key: "announcements",    label: "Announcements",      description: "Broker-wide announcement banner + history.", category: "ops" },
  { key: "category-manager", label: "Category Manager",   description: "Free-form tags across traders / leads / IBs.", category: "ops" },
  { key: "reports",          label: "Reports",            description: "Activity, deposits, withdrawals, commissions, ledger.", category: "ops" },

  // IB / Trading
  { key: "ib-management",    label: "IB Management",      description: "IB tree, commissions, registration, analysis.", category: "trading" },
  { key: "ai-lead-scoring",  label: "AI Lead Scoring",    description: "ML-scored lead pipeline.", category: "ai" },
  { key: "manage-trade",     label: "Manage Trade",       description: "Trading reports + commission status (MT5).", category: "trading" },
  { key: "manage-accounts",  label: "Manage Accounts",    description: "Live + demo MT5 accounts.", category: "trading" },
  { key: "manage-banks",     label: "Manage Banks",       description: "Company bank list for deposits.", category: "finance" },
  { key: "social-trade",     label: "Social Trade (PAMM)", description: "PAMM / MAMM strategy routing.", category: "trading" },
  { key: "ea-marketplace",   label: "EA Marketplace",     description: "Algorithmic trading EAs.", category: "trading" },
  { key: "copy-trading",     label: "Copy Trading",       description: "Strategy providers (masters) + followers who auto-copy trades.", category: "trading" },
  { key: "mt5",              label: "MT5 Manager",        description: "MT5 Manager API connection + tools.", category: "trading" },
  { key: "copy-symbols",     label: "Symbol Whitelist",   description: "Which MT5 symbols traders can see.", category: "trading" },

  // Finance
  { key: "finance",          label: "Finance",            description: "Balance, credit, fund management.", category: "finance" },
  { key: "fund-transfer",    label: "Fund Transfer",      description: "Internal + external transfer monitoring.", category: "finance" },
  { key: "offers",           label: "Offers & Bonus",     description: "Bonus packages, grants, loyalty.", category: "finance" },
  { key: "group-settings",   label: "Group Settings",     description: "Trading groups (standard / IB / CPA).", category: "finance" },

  // Compliance / Risk
  { key: "risk-management",  label: "Risk Management",    description: "Exposure, anti-hedge, suspicious activity.", category: "compliance" },
  { key: "compliance",       label: "Compliance",         description: "FATCA, CRS, MiFID, AML audit, SAR, sanctions.", category: "compliance" },

  // Marketing / engagement
  { key: "contest",          label: "Trading Contest",    description: "Contests + leaderboards + prize payouts.", category: "marketing" },

  // AI / external integrations
  { key: "ml-predictions",   label: "ML Predictions",     description: "Churn, LTV, deposit likelihood models.", category: "ai" },
  { key: "whatsapp-console", label: "WhatsApp Console",   description: "Unified WhatsApp inbox for sales / support.", category: "ai" },

  // White-label
  { key: "white-label",      label: "White-Label",        description: "Cross-tenant tooling (platform-side only).", category: "ops" },
];

export const MODULES_BY_KEY: Record<string, ModuleDef> = Object.fromEntries(
  MODULE_REGISTRY.map((m) => [m.key, m]),
);

/** Returns the full set of enabled module keys, defaulting to ALL when
 * the tenant has no explicit modules config (so existing tenants stay
 * unaffected by the introduction of this feature). */
export function resolveEnabledModules(modulesConfig: string[] | undefined): Set<string> {
  // No config → all modules enabled.
  if (!modulesConfig || modulesConfig.length === 0) {
    return new Set(MODULE_REGISTRY.map((m) => m.key));
  }
  const enabled = new Set(modulesConfig);
  // Core modules are always present, regardless of config.
  MODULE_REGISTRY.filter((m) => m.core).forEach((m) => enabled.add(m.key));
  return enabled;
}

/** Trader sidebar items → module keys. Items not in the map are
 * always visible (Dashboard, My Profile, Account Settings — these are
 * inherent to the trader portal regardless of broker feature flags). */
export const TRADER_NAV_LABEL_TO_MODULE: Record<string, string> = {
  "Banks":            "manage-banks",
  "Trade Accounts":   "manage-accounts",
  "Trade History":    "manage-trade",
  "Deposit Funds":    "finance",
  "Withdraw Funds":   "finance",
  "Fund Transfer":    "fund-transfer",
  "Bonus & Loyalty":  "offers",
  "Copy Trading":     "copy-trading",
  "Become an IB":     "ib-management",
  "Affiliate":        "ib-management",
  "Trading Contest":  "contest",
  "EA Marketplace":   "ea-marketplace",
  "Gamification":     "contest",
  "KYC Verification": "kyc-management",
  "Support Tickets":  "support",
  "AI Chatbot":       "whatsapp-console",
};

/** Map every top-level nav item to one of the module keys. The sidebar
 * filter uses this to decide whether to render that item. */
export const NAV_LABEL_TO_MODULE: Record<string, string> = {
  "Dashboard":                "dashboard",
  "Admin Profile":            "admin-profile",
  "Manage Admin":             "manage-admin",
  "Manage Trader":            "manage-client",
  "Manage Request":           "manage-request",
  "KYC Management":           "kyc-management",
  "Lead Management":          "lead-management",
  "Manager Settings":         "manager-settings",
  "Support Tickets":          "support",
  "Email (Bulk)":             "email",
  "Announcements":            "announcements",
  "Category Manager":         "category-manager",
  "Reports":                  "reports",
  "IB Management":            "ib-management",
  "AI Lead Scoring":          "ai-lead-scoring",
  "Manage Trade":             "manage-trade",
  "Manage Accounts":          "manage-accounts",
  "Manage Banks":             "manage-banks",
  "Social Trade":             "social-trade",
  "EA Marketplace":           "ea-marketplace",
  "MT5 Manager API":          "mt5",
  "Finance":                  "finance",
  "Fund Transfer":            "fund-transfer",
  "Offers":                   "offers",
  "Group Settings":           "group-settings",
  "Risk Management":          "risk-management",
  "Compliance":               "compliance",
  "Contest":                  "contest",
  "ML Predictions":           "ml-predictions",
  "WhatsApp Console":         "whatsapp-console",
  "White-Label":              "white-label",
  "Settings":                 "settings",
};
